import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import BDPLayout from "../../components/bdp/BDPLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Copy,
  XCircle,
  LinkIcon,
  CheckCircle,
  Clock,
  Loader2,
  ExternalLink,
} from "lucide-react";

const BDPPaymentLinks = () => {
  const { API, token, user } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [cancelling, setCancelling] = useState(null);

  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    engagement_type: "one_time",
    notes: "",
  });
  const [lineItems, setLineItems] = useState([{ description: "", amount: "" }]);

  const fetchLinks = async () => {
    try {
      const r = await fetch(`${API}/bdp/payment-links`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setLinks(await r.json());
    } catch { // error handled silently
      console.error("Request failed:", error);
    }
    setLoading(false);
  };

  useEffect(() => { fetchLinks(); }, [API, token]);

  const addLineItem = () => setLineItems([...lineItems, { description: "", amount: "" }]);
  const removeLineItem = (i) => setLineItems(lineItems.filter((_, idx) => idx !== i));
  const updateLineItem = (i, field, value) => {
    const items = [...lineItems];
    items[i][field] = value;
    setLineItems(items);
  };

  const total = lineItems.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);
  const commRate = form.engagement_type === "monthly" ? 0.15 : 0.20;
  const commAmount = total * commRate;

  const handleCreate = async () => {
    if (!form.client_name || !form.client_email || lineItems.some((li) => !li.description || !li.amount)) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (total <= 0) { toast.error("Total must be greater than 0"); return; }
    if (total > 15000 && user?.role !== "super_admin") {
      toast.error("Amount exceeds $15,000 limit");
      return;
    }

    setCreating(true);
    try {
      const r = await fetch(`${API}/bdp/payment-links`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          agent_code: user?.agent_code,
          line_items: lineItems.map((li) => ({ description: li.description, amount: parseFloat(li.amount) })),
        }),
      });
      const data = await r.json();
      if (r.ok) {
        toast.success("Payment link created!");
        setCreateOpen(false);
        setForm({ client_name: "", client_email: "", engagement_type: "one_time", notes: "" });
        setLineItems([{ description: "", amount: "" }]);
        fetchLinks();
      } else {
        toast.error(data.detail || "Failed to create link");
      }
    } catch { toast.error("Network error"); }
    setCreating(false);
  };

  const handleCancel = async (linkId) => {
    if (!window.confirm("Are you sure you want to void this payment link?")) return;
    setCancelling(linkId);
    try {
      const r = await fetch(`${API}/bdp/payment-links/${linkId}/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        toast.success("Payment link cancelled");
        fetchLinks();
      } else {
        const d = await r.json();
        toast.error(d.detail || "Failed to cancel");
      }
    } catch { toast.error("Network error"); }
    setCancelling(null);
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  const filtered = links.filter((l) => l.status === tab);
  const tabs = [
    { key: "active", label: "Outstanding", count: links.filter((l) => l.status === "active").length },
    { key: "paid", label: "Completed", count: links.filter((l) => l.status === "paid").length },
    { key: "cancelled", label: "Cancelled", count: links.filter((l) => l.status === "cancelled").length },
  ];

  const statusBadge = (status) => {
    switch (status) {
      case "active": return <Badge className="bg-amber-50 text-amber-700 border-amber-200">Outstanding</Badge>;
      case "paid": return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Paid</Badge>;
      case "cancelled": return <Badge className="bg-gray-100 text-gray-500 border-gray-200">Cancelled</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <BDPLayout>
      <div className="space-y-5" data-testid="bdp-payment-links">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-[#1A2636]">Payment Links</h1>
          <Button onClick={() => setCreateOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="create-link-btn">
            <Plus className="w-4 h-4" /> Create Link
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                tab === t.key ? "bg-white shadow-sm text-[#1A2636]" : "text-gray-500 hover:text-gray-700"
              }`}
              data-testid={`tab-${t.key}`}
            >
              {t.label} <span className="ml-1 text-xs opacity-60">({t.count})</span>
            </button>
          ))}
        </div>

        {/* Links list */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : filtered.length ? (
          <div className="space-y-3">
            {filtered.map((link) => (
              <Card key={link.link_id} className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-semibold text-[#1A2636]">{link.client_name}</p>
                        {statusBadge(link.status)}
                        <Badge variant="outline" className="text-[10px]">
                          {link.engagement_type === "monthly" ? "Monthly" : "One-time"}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500">{link.client_email}</p>
                      <div className="text-xs text-gray-400 mt-1">
                        {link.line_items?.map((li, i) => (
                          <span key={li.description || i}>{li.description}{i < link.line_items.length - 1 ? " · " : ""}</span>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        Created {new Date(link.created_at).toLocaleDateString()}
                        {link.paid_at && ` · Paid ${new Date(link.paid_at).toLocaleDateString()}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-lg font-bold text-[#1A2636]">${Number(link.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                        <p className="text-xs text-emerald-600">
                          {Math.round(link.commission_rate * 100)}% → ${Number(link.commission_amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        {link.status === "active" && (
                          <>
                            <Button variant="outline" size="sm" onClick={() => copyLink(link.stripe_url)} title="Copy link" data-testid={`copy-${link.link_id}`}>
                              <Copy className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => window.open(link.stripe_url, "_blank")} title="Open link">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCancel(link.link_id)}
                              disabled={cancelling === link.link_id}
                              title="Void link"
                              className="text-red-500 hover:text-red-700"
                              data-testid={`cancel-${link.link_id}`}
                            >
                              {cancelling === link.link_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-400">
            <LinkIcon className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No {tab} payment links</p>
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Payment Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Client Name *</Label>
                <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Client name" data-testid="pl-client-name" />
              </div>
              <div>
                <Label className="text-xs">Client Email *</Label>
                <Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="email@company.com" data-testid="pl-client-email" />
              </div>
            </div>

            <div>
              <Label className="text-xs">Engagement Type *</Label>
              <Select value={form.engagement_type} onValueChange={(v) => setForm({ ...form, engagement_type: v })}>
                <SelectTrigger data-testid="pl-engagement-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_time">One-Time Project (20% commission)</SelectItem>
                  <SelectItem value="monthly">Monthly Engagement (15% commission)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Line Items *</Label>
                <Button variant="ghost" size="sm" onClick={addLineItem} className="h-7 text-xs gap-1 text-emerald-600">
                  <Plus className="w-3 h-3" /> Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {lineItems.map((li, i) => (
                  <div key={li.description || i} className="flex gap-2 items-start">
                    <Input
                      value={li.description}
                      onChange={(e) => updateLineItem(i, "description", e.target.value)}
                      placeholder="Description"
                      className="flex-1"
                      data-testid={`li-desc-${i}`}
                    />
                    <Input
                      type="number"
                      value={li.amount}
                      onChange={(e) => updateLineItem(i, "amount", e.target.value)}
                      placeholder="Amount"
                      className="w-28"
                      data-testid={`li-amount-${i}`}
                    />
                    {lineItems.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeLineItem(i)} className="h-10 px-2 text-red-400">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Internal notes..." rows={2} />
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total</span>
                <span className="font-bold text-[#1A2636]">${total.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Commission ({Math.round(commRate * 100)}%)</span>
                <span className="font-semibold text-emerald-600">${commAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
              </div>
              {total > 15000 && user?.role !== "super_admin" && (
                <p className="text-xs text-red-500 mt-1">Exceeds $15,000 BDP limit</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="pl-submit">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate Payment Link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </BDPLayout>
  );
};

export default BDPPaymentLinks;
